import React, { useContext, useState } from 'react'
import styled from '@emotion/styled'
import { useLocation } from '@reach/router'
import PropTypes from 'prop-types'
import QueryString from 'qs'

import { PricingType } from '../common/PropTypes'

// utils
import {
	buildDataForPushEventStreamData,
	getDistanceFromTop,
	makeTransactionId,
	pushEventStreamData,
	pushGtmData,
	generateLinkWithAddedParams,
	generateAffiliateLink,
	replaceSpacesWithDashes,
} from '@jelly/utils'

// Import Components
import { Modal } from '../Modal'
import RfqWidget from '../RfqWidget'
import { SiteConfigContext } from '../SiteConfigProvider'
import { Button } from '@jelly/ui'
import { PhoneIcon } from '../svgs/PhoneIcon'
import { useQt } from '@jelly/utils'

const DEFAULT_AFFILIATE_TEXT = 'visit site'
const DEFAULT_WIDGET_TEXT = 'compare quotes'

const ButtonsContainer = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	width: 100%;
	max-width: 180px;

	${({ theme }) => theme.MQ.M} {
		align-items: flex-start;
	}

	button :not(:first-child),
	a :not(:first-child) {
		margin-top: 1rem;
	}
`

/**
 * This is the Monetization Button Component
 * Baked in logic to display more than one button if supplied by BE
 *
 * @param {categoryWidgetId} props.categoryWidgetId :: widget id declared on the category level
 * if this does not exist then button falls back on categoryWidgetId
 */
export const MonetizationButton = (props) => {
	const {
		buttonLimit,
		categoryWidgetId,
		fallbackComponent = null,
		onRfqButtonClick = () => {},
		onQtButtonClick = () => {},
		pricing,
		rank = '',
		sectionType = '',
		serviceName = '',
		tabTitle,
		templateType = '',
		type,
	} = props
	const affiliateLink = generateAffiliateLink(pricing)

	const [showRfqModal, setShowRfqModal] = useState(false)

	const { offer_id: offerId, aff_id: affId } = QueryString.parse(affiliateLink?.split('?')[1])

	const { ringpoolDefaultPhone, ringpoolId, isCapped } = useQt({ affId, offerId })

	const {
		siteConstants: { SITE_HOST },
	} = useContext(SiteConfigContext)

	const routerLocation = useLocation()

	const handleCtaClick = (e, buttonType, link) => {
		e.preventDefault()

		if (buttonType === 'affiliate' && !link) {
			return console.warn(`Missing ${serviceName} affiliate button link`)
		}

		const bdcTransactionId = makeTransactionId()
		const bodyHeight = document.documentElement.scrollHeight
		const buttonHeight = getDistanceFromTop(e.target)
		const buttonPosition = Math.round((buttonHeight / bodyHeight) * 100)

		pushGtmData({
			event: 'click.buyButton',
			merchant: serviceName,
			placement: 'bestPickCard',
			position: `${buttonPosition}%`,
			slug: location.href, //eslint-disable-line
			transactionId: bdcTransactionId,
		})

		pushEventStreamData(
			buildDataForPushEventStreamData(window.location, e.currentTarget, serviceName, buttonPosition)
		)

		// external redirect with noopener protection - https://mathiasbynens.github.io/rel-noopener/
		if (buttonType === 'affiliate') {
			const linkToOpen = `${link.replace(/TX-ID-VALUE/g, bdcTransactionId)}`
			const newWindow = window.open()
			newWindow.opener = null
			newWindow.location = linkToOpen
		} else {
			setShowRfqModal(true)
		}
	}

	const setUniqueButtonId = (text) => {
		return replaceSpacesWithDashes(`${templateType}_${serviceName}_${sectionType}_${text}`)
	}

	let widgetCategoryId = categoryWidgetId
	if (pricing) {
		if (pricing?.widget?.isExclusive) {
			widgetCategoryId = pricing.widget.id
		} else {
			widgetCategoryId = categoryWidgetId || pricing.widgetId
		}
	}

	const generateWidgetButton = ({ buttonText = DEFAULT_WIDGET_TEXT, inverseButton = false }) => {
		const handleClick = (e) => {
			onRfqButtonClick()
			handleCtaClick(e, 'widget')
		}
		return (
			<Button
				className="monetization-button"
				id={setUniqueButtonId(buttonText)}
				onClick={handleClick}
				type="button"
				variant={inverseButton ? 'secondary' : 'primary'}
			>
				{buttonText}
			</Button>
		)
	}

	const generateRingpoolPhoneButton = (variant = 'primary') => {
		if (!(ringpoolDefaultPhone && ringpoolId)) {
			return null
		}
		return (
			<Button
				className="monetization-button"
				href={`tel:${ringpoolDefaultPhone}`}
				ringpoolid={ringpoolId}
				placeholder={ringpoolDefaultPhone}
				type="button"
				variant={variant}
			>
				<PhoneIcon height="20px" width="20px" /> &nbsp;
				{ringpoolDefaultPhone}
			</Button>
		)
	}

	const generateAffiliateButton = ({
		buttonText = DEFAULT_AFFILIATE_TEXT,
		inverseButton = false,
		isAddQtParams = true,
		link,
	}) => {
		if (isCapped) {
			return null
		}

		const handleClick = (e) => {
			onQtButtonClick()
			handleCtaClick(e, buttonType, affiliateLinkCheck)
		}

		const buttonType = 'affiliate'

		const affiliateLinkCheck = isAddQtParams
			? generateLinkWithAddedParams({
					clientLocation: `${SITE_HOST}${routerLocation.pathname}`,
					link: link,
					rank: rank,
					type: type,
					tabTitle: tabTitle,
			  })
			: link

		return (
			<>
				<Button
					className="monetization-button"
					id={setUniqueButtonId(buttonText)}
					href={affiliateLinkCheck}
					onClick={handleClick}
					rel="sponsored nofollow noopener"
					variant={inverseButton || ringpoolDefaultPhone ? 'secondary' : 'primary'}
				>
					{buttonText}
				</Button>
			</>
		)
	}

	const generateDefaultWidgetButton = () => {
		return widgetCategoryId ? generateWidgetButton({}) : null
	}

	const getButtonList = () => {
		let buttonArray = []

		const { affiliate, selected, widget } = pricing || {}
		switch (selected) {
			case 'affiliate':
				if (isCapped || !affiliate) {
					buttonArray.push(generateDefaultWidgetButton())
				} else {
					buttonArray.push(generateRingpoolPhoneButton())
					buttonArray.push(
						generateAffiliateButton({
							buttonText: affiliate.cta,
							link: affiliateLink,
							isAddQtParams: affiliate.isAddQtParams,
						})
					)
				}
				break
			case 'affiliate-widget':
				if (!affiliate) {
					buttonArray.push(generateDefaultWidgetButton())
				} else {
					buttonArray.push(generateRingpoolPhoneButton())
					buttonArray.push(
						generateAffiliateButton({
							buttonText: affiliate.cta,
							link: affiliateLink,
							isAddQtParams: affiliate.isAddQtParams,
						})
					)

					if (widget) {
						buttonArray.push(
							generateWidgetButton({
								buttonText: widget.cta,
								inverseButton: !isCapped,
							})
						)
					}
				}
				break
			case 'widget':
				buttonArray.push(
					!widget ? generateDefaultWidgetButton() : generateWidgetButton({ buttonText: widget.cta })
				)
				break
			case 'widget-affiliate':
				if (!widget) {
					buttonArray.push(generateDefaultWidgetButton())
				} else {
					buttonArray.push(
						generateWidgetButton({
							buttonText: widget.cta,
						})
					)

					buttonArray.push(generateRingpoolPhoneButton('secondary'))
					buttonArray.push(
						generateAffiliateButton({
							buttonText: affiliate.cta,
							link: affiliateLink,
							inverseButton: true,
							isAddQtParams: affiliate.isAddQtParams,
						})
					)
				}
				break
			default:
				buttonArray.push(generateDefaultWidgetButton())
				break
		}

		// clean out all the nulls and undefined
		buttonArray = buttonArray.filter((item) => !(item === null || item === undefined))

		// if there are no monetization buttons render the fallback component if there is one
		if (!buttonArray.length) {
			return fallbackComponent
		}

		// if a buttonLimit was passed (like from bestpicks) trim the buttonArrray down
		if (buttonLimit) {
			buttonArray = buttonArray.slice(0, buttonLimit)
		}

		return buttonArray
	}

	const buttonList = getButtonList()

	if (!buttonList || !buttonList.length) {
		return null
	}

	return (
		<>
			<Modal
				isModalVisible={showRfqModal}
				setIsModalVisible={setShowRfqModal}
				onRequestClose={() => setShowRfqModal(false)}
				closeIconPadding={'10px'}
			>
				<RfqWidget widgetCategoryId={widgetCategoryId} />
			</Modal>
			<ButtonsContainer>
				{buttonList.map((button, i) => {
					return <React.Fragment key={i}> {button} </React.Fragment>
				})}
			</ButtonsContainer>
		</>
	)
}

MonetizationButton.propTypes = {
	categoryWidgetId: PropTypes.string,
	pricing: PricingType,
	rank: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	sectionType: PropTypes.string,
	serviceName: PropTypes.string,
	tabTitle: PropTypes.string,
	templateType: PropTypes.string,
	type: PropTypes.string,
}
